import {
  EngineWorkerCancelMessage,
  EngineWorkerMessage,
  EngineWorkerRequest,
  EngineWorkerRequestPayload,
  EngineWorkerRequestType,
  EngineWorkerResponseMap,
} from "../protocol";
import { createPendingRequests } from "./createPendingRequests";
import { EngineWorkerError } from "./EngineWorkerError";
import { abortable, createDeferred } from "./promiseHelpers";

const INIT_TIMEOUT_MS = 60_000;
const MAX_RESTARTS = 3;
const RESTART_DELAY_MS = 1_000;

export type EngineWorkerClient = ReturnType<typeof createEngineWorkerClient>;

export type EngineWorkerState =
  | { error: Error; status: "failed" }
  | { status: "loading" }
  | { status: "ready" };

type EngineWorkerClientOptions = {
  initTimeoutMs?: number;
  maxRestarts?: number;
  restartDelayMs?: number;
};

export function createEngineWorkerClient(
  createWorker: () => Worker,
  options?: EngineWorkerClientOptions,
) {
  const initTimeoutMs = options?.initTimeoutMs ?? INIT_TIMEOUT_MS;
  const maxRestarts = options?.maxRestarts ?? MAX_RESTARTS;
  const restartDelayMs = options?.restartDelayMs ?? RESTART_DELAY_MS;

  const listeners = new Set<() => void>();
  const pending = createPendingRequests();
  let state: EngineWorkerState = { status: "loading" };
  let ready = createReadyDeferred();
  let worker: Worker | null = null;
  let started = false;
  let consecutiveFailures = 0;
  let initTimer: ReturnType<typeof setTimeout> | undefined;
  let restartTimer: ReturnType<typeof setTimeout> | undefined;

  function setState(next: EngineWorkerState) {
    state = next;
    listeners.forEach((listener) => listener());
  }

  function markLoading() {
    if (state.status === "loading") return;
    ready = createReadyDeferred();
    setState({ status: "loading" });
  }

  function markReady() {
    clearTimeout(initTimer);
    consecutiveFailures = 0;
    ready.resolve();
    setState({ status: "ready" });
  }

  function markFailed(cause: unknown) {
    const error = new EngineWorkerError("Engine worker failed to start", {
      cause,
    });
    ready.reject(error);
    setState({ error, status: "failed" });
  }

  function initWorker() {
    if (!started) return;

    try {
      worker = createWorker();
    } catch (error) {
      handleCrash(error);
      return;
    }

    const current = worker;
    current.addEventListener("message", (event: MessageEvent) => {
      if (current === worker) handleMessage(event.data);
    });
    // Fired when the worker script fails to load/evaluate (e.g. WASM fetch failure) or on an uncaught error.
    current.addEventListener("error", (event) => {
      if (current !== worker) return;
      event.preventDefault();
      handleCrash(event.error ?? new Error(event.message));
    });

    initTimer = setTimeout(
      () =>
        handleCrash(
          new EngineWorkerError("Engine worker initialization timed out"),
        ),
      initTimeoutMs,
    );
  }

  function terminate(reason: Error) {
    clearTimeout(initTimer);
    worker?.terminate();
    worker = null;
    pending.rejectAll(reason);
  }

  function handleCrash(cause: unknown) {
    terminate(new EngineWorkerError("Engine worker crashed", { cause }));
    consecutiveFailures++;

    if (consecutiveFailures > maxRestarts) {
      markFailed(cause);
      return;
    }

    markLoading();
    restartTimer = setTimeout(initWorker, restartDelayMs * consecutiveFailures);
  }

  function handleMessage(message: EngineWorkerMessage) {
    switch (message.type) {
      case "error":
        pending.reject(
          message.correlationId,
          EngineWorkerError.fromSerialized(message.error),
        );
        break;
      case "fatal":
        handleCrash(EngineWorkerError.fromSerialized(message.error));
        break;
      case "initialized":
        markReady();
        break;
      case "response":
        pending.resolve(message.correlationId, message.result);
        break;
    }
  }

  function start() {
    if (started) return;
    started = true;
    consecutiveFailures = 0;
    markLoading();
    initWorker();
  }

  function stop() {
    if (!started) return;
    started = false;
    clearTimeout(restartTimer);
    terminate(new EngineWorkerError("Engine worker was stopped"));
    markLoading();
  }

  async function request<T extends EngineWorkerRequestType>(
    type: T,
    payload: EngineWorkerRequestPayload<T>,
    { signal }: { signal?: AbortSignal } = {},
  ): Promise<EngineWorkerResponseMap[T]> {
    await abortable(ready.promise, signal);

    const target = worker;
    if (!target) throw new EngineWorkerError("Engine worker is not running");

    const correlationId = payload.correlationId ?? crypto.randomUUID();
    const response = pending.add(correlationId);
    const message = { ...payload, correlationId, type };
    target.postMessage(message as unknown as EngineWorkerRequest);

    try {
      return (await abortable(response, signal)) as EngineWorkerResponseMap[T];
    } catch (error) {
      if (signal?.aborted) {
        pending.delete(correlationId);
        target.postMessage({
          correlationId,
          type: "cancel",
        } satisfies EngineWorkerCancelMessage);
      }
      throw error;
    }
  }

  return {
    getState: () => state,
    request,
    start,
    stop,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    waitUntilReady: () => ready.promise,
  };
}

function createReadyDeferred() {
  const deferred = createDeferred<void>();
  deferred.promise.catch(() => {});
  return deferred;
}
